import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import {
  CurrencyValue,
  BarChart,
  formatDatetime,
  formatNumber
} from '@blockanalitica/ui';
import { ReactNode, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useChartPeriod } from '@/hooks';

export default function HistoricGraphs({
  data,
  type,
  timeFilter,
  chartDaysAgo
}: {
  type: string;
  timeFilter: ReactNode;
  chartDaysAgo: string;
  data: SimpleChartData[];
}) {
  const theme = useTheme();

  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const datasets = useMemo(() => {
    let datasets: SimpleChartDataset[];

    if (type === 'draw_wipe') {
      datasets = [
        {
          xField: 'date',
          yField: 'draw_volume',
          label: 'Draw',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'wipe_volume',
          label: 'Wipe',
          backgroundColor: theme.colors.chartDown
        }
      ];
    } else {
      datasets = [
        {
          xField: 'date',
          yField: 'lock_volume',
          label: 'Lock',
          backgroundColor: theme.colors.chartUp
        },
        {
          xField: 'date',
          yField: 'free_volume',
          label: 'Free',
          backgroundColor: theme.colors.chartDown
        }
      ];
    }

    return datasets;
  }, [type, theme.colors]);
  const currency = type === 'draw_wipe' ? 'MKR' : 'USDS';
  const valueFormatter = useCallback(
    (value: number) => (
      <CurrencyValue
        value={value}
        options={{ currency: currency, iconSize: '0' }}
      />
    ),
    [currency]
  );

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  return (
    <BarChart
      key={type}
      data={data}
      datasets={datasets}
      valueFormatter={valueFormatter}
      timeFilter={timeFilter}
      config={config}
      yScaleValueFormatter={(value: number) => formatNumber(value)}
      subValueFormatter={subValueFormatter}
    />
  );
}
